import { getAccessToken } from '@/services/auth-service'
import { getAuthHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

const API_BASE = `${env.backendApiUrl}/api/v1/bug-reports`
const UPLOAD_ENDPOINT = `${env.backendApiUrl}/api/v1/storage/upload/bug-reports`
const ASSETS_BASE = `${env.backendApiUrl}/api/v1/storage/assets/bugs`

/** Shared across all wizard steps: screenshots, recordings and documents draw
 *  from the same budget. Raised from 3 with the document step (#475) — the
 *  typical report is a screenshot AND the file that triggered the bug, which
 *  the old budget could barely fit. Must stay <= BUG_REPORT_MAX_FILES in the
 *  BFF, which is the authority. */
const MAX_ATTACHMENTS = 6
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

const MEDIA_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
] as const

/** PDF, Word and Excel — the sample file that caused the bug (#475). */
const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

/** Browsers derive `File.type` from an OS lookup, and Windows without the
 *  Office registry entries reports "application/octet-stream" (or "") for a
 *  valid .docx. Validating on MIME alone would reject exactly the files this
 *  feature exists for, so a known document extension counts on its own. The
 *  BFF applies the same fallback and normalises the stored type. */
const DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'] as const

/** `accept` value for the document file input. Lists both extensions and MIME
 *  types so the picker filters correctly regardless of the OS mapping. */
export const DOCUMENT_ACCEPT = [...DOCUMENT_EXTENSIONS, ...DOCUMENT_MIME_TYPES].join(',')

const ALLOWED_MIME_TYPES = [...MEDIA_MIME_TYPES, ...DOCUMENT_MIME_TYPES] as const

function hasDocumentExtension(fileName: string): boolean {
  const lower = fileName.toLowerCase()
  return DOCUMENT_EXTENSIONS.some((extension) => lower.endsWith(extension))
}

/** True for anything the document step accepts. */
export function isDocumentFile(file: File): boolean {
  return (
    (DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type) ||
    hasDocumentExtension(file.name)
  )
}

/** True for anything the upload accepts at all — media by MIME type, documents
 *  by MIME type or extension. */
export function isAllowedAttachment(file: File): boolean {
  return (ALLOWED_MIME_TYPES as readonly string[]).includes(file.type) || hasDocumentExtension(file.name)
}

/** True for Word attachments — previewed through the server-side PDF
 *  conversion (`.doc` and `.docx` alike). */
export function isWordAttachment(attachment: BugReportAttachment): boolean {
  const lower = attachment.name.toLowerCase()
  return lower.endsWith('.doc') || lower.endsWith('.docx')
}

/** True for attachments previewed through the server-side PDF conversion:
 *  Word, and Excel as one page per sheet (cdb_tqs#2162). */
export function isConvertedAttachment(attachment: BugReportAttachment): boolean {
  return isWordAttachment(attachment) || attachment.name.toLowerCase().endsWith('.xlsx')
}

/** Classifies a stored attachment (Cortex detail view). Works off the MIME
 *  type the BFF normalised on upload, with the filename as a safety net for
 *  rows written before that normalisation existed. */
export function isDocumentAttachment(attachment: BugReportAttachment): boolean {
  return (
    (DOCUMENT_MIME_TYPES as readonly string[]).includes(attachment.type) ||
    hasDocumentExtension(attachment.name)
  )
}

/** Window event that opens the ticket panel (#1170) as "Fehler melden" —
 *  Spotlight, the standalone rail, "Bug melden" in "Meine Tickets". */
export const OPEN_BUG_REPORT_EVENT = 'open-bug-report'
/** Window event that opens the ticket panel preset to "Verbesserung vorschlagen" (Spotlight lightbulb). */
export const OPEN_FEATURE_REQUEST_EVENT = 'open-feature-request'
/** Window event the ticket panel fires after a ticket was filed
 *  (`detail.reportId`), so "Meine Tickets" can list and open it (#1098). */
export const BUG_REPORT_SUBMITTED_EVENT = 'bug-report-submitted'
/** Window event fired after the caller's read marker moved (marked read, or
 *  posted — writing marks the thread read), so the Cortex badge recounts (#1135). */
export const BUG_REPORT_READ_EVENT = 'bug-report-read'

function announceRead(reportId: string): void {
  window.dispatchEvent(new CustomEvent(BUG_REPORT_READ_EVENT, { detail: { reportId } }))
}

/** `waiting_for_reporter` ("Rückfrage", #1094): the team asked the reporter
 *  something; the reporter's next reply moves the report back to
 *  `in_progress` (server-side). `planned` ("Geplant", #1168) exists for
 *  feature tickets only — the BFF answers 422 `planned_not_allowed` on a bug. */
export type BugReportStatus = 'open' | 'in_progress' | 'waiting_for_reporter' | 'planned' | 'resolved' | 'closed'

/** The statuses of a bug, in workflow order. */
export const BUG_REPORT_STATUSES: BugReportStatus[] = [
  'open',
  'in_progress',
  'waiting_for_reporter',
  'resolved',
  'closed',
]

/** Every ticket status in workflow order (#1168); `planned` only for features.
 *  "Geplant" sits between "Offen" and "In Bearbeitung" — decided, not started
 *  (the column order of the Tickets board, #1171). */
export const TICKET_STATUSES: BugReportStatus[] = [
  'open',
  'planned',
  'in_progress',
  'waiting_for_reporter',
  'resolved',
  'closed',
]

/** Bug reports and feature requests are one ticket since #1168. API paths and
 *  names keep "bug report"; only the UI says "Ticket". */
export type TicketType = 'bug' | 'feature'
export const TICKET_TYPES: TicketType[] = ['bug', 'feature']

/** Why a ticket was closed (#1168). Required on every close; the auto-close
 *  after 14 days without an answer (#1107) writes `no_response`. A reopen
 *  clears it. */
export type TicketCloseReason = 'done' | 'declined' | 'duplicate' | 'not_a_bug' | 'no_response'
export const TICKET_CLOSE_REASONS: TicketCloseReason[] = ['done', 'declined', 'duplicate', 'not_a_bug', 'no_response']

/** The statuses a ticket of this type can have. */
export function statusesForType(type: TicketType): BugReportStatus[] {
  return type === 'feature' ? TICKET_STATUSES : BUG_REPORT_STATUSES
}

export type BugReportCategory = 'ui' | 'functionality' | 'performance' | 'data_loss' | 'other'
/** Categories of a feature ticket — the former feature-request categories. */
export type FeatureCategory = 'new_feature' | 'improvement' | 'ui_ux' | 'workflow' | 'integration' | 'other'
export type TicketCategory = BugReportCategory | FeatureCategory

export const FEATURE_CATEGORIES: FeatureCategory[] = [
  'new_feature',
  'improvement',
  'ui_ux',
  'workflow',
  'integration',
  'other',
]
/** Every value the API can return. Nobody picks it any more (#1170): the
 *  ticket panel sends `webos` in the desktop and `chat` under the shell-free
 *  routes, and the finer "where" goes into `context`. `portal` and
 *  `document_editor` come from older reports; `pdb` is historical (the
 *  separate PDB frontend was decommissioned in #751) and kept only so old
 *  reports stay readable in Cortex. */
export type BugReportSystem = 'portal' | 'webos' | 'document_editor' | 'pdb' | 'chat'

export const BUG_REPORT_CATEGORIES: BugReportCategory[] = [
  'ui',
  'functionality',
  'performance',
  'data_loss',
  'other',
]

/** The categories a ticket of this type may carry; the BFF refuses others (422). */
export function categoriesForType(type: TicketType): TicketCategory[] {
  return type === 'feature' ? FEATURE_CATEGORIES : BUG_REPORT_CATEGORIES
}

/**
 * What the client captured when the ticket was filed (#1168) — filled
 * automatically by the submission panel (#1170), opt-out possible. The BFF
 * keeps only these keys (others are dropped), caps each string and the whole
 * object (4 KB, 422 above). Display only, visible to admins and the reporter;
 * never used for an access decision (the ticket's organisation is `client_id`
 * from the caller's profile, not `client_name` here).
 */
export interface TicketContext {
  system?: string
  app?: string
  window?: string
  route?: string
  app_version?: string
  browser?: string
  os?: string
  client_name?: string
  /** ISO 8601. */
  captured_at?: string
}

export interface BugReportAttachment {
  name: string
  path: string
  size: number
  type: string
}

export interface BugReport {
  id: string
  /** #1168: `bug` or `feature`; changed by an admin via `changeType`. */
  type: TicketType
  title: string
  description: string
  /** One of `categoriesForType(type)`. */
  category: TicketCategory
  system: BugReportSystem
  status: BugReportStatus
  /** #1168: set exactly while `status` is `closed`. */
  close_reason: TicketCloseReason | null
  /** #1168: captured on submission; `null` for older tickets and opt-outs. */
  context: TicketContext | null
  /**
   * Organisation the report was submitted from (#972). `null` for reports
   * written before the column existed and for callers without a user profile
   * (API keys, training participants).
   */
  client_id: string | null
  client_name: string | null
  /**
   * Who filed the report (#1092), set by the BFF from the caller. Only ever
   * returned to admins and to the reporter. `null` for reports filed before
   * #1092, by API keys / training participants, or after the user's deletion.
   */
  reporter_user_id: string | null
  reporter_name: string | null
  attachments: BugReportAttachment[]
  /**
   * The latest internal note (#1094 — notes live in the thread now). Internal
   * to the team; always `null` when the reporter reads the report.
   */
  admin_notes: string | null
  created_at: string
  updated_at: string
  /** Last transition to `closed` (#1094); retention (#1107) counts from here. */
  closed_at: string | null
  /** Replies by others since the caller last read the thread (#1094). */
  unread_count: number
  /** Creation or the newest message the caller may see (#1094). */
  last_activity_at: string | null
  /** Whether the caller may post to the thread now (#1094) — `false` on a
   *  closed report for everyone; disable the composer then. */
  can_post: boolean
  /** The GitHub issue created from this report (#1136). Admins only — absent
   *  or `null` for the reporter and for reports without an issue. */
  github_issue_number?: number | null
  github_issue_url?: string | null
  github_repo?: string | null
  github_created_at?: string | null
  github_created_by?: string | null
}

/** Whether this environment can create GitHub issues (#1136), and where. */
export interface BugReportGitHubConfig {
  available: boolean
  repos: string[]
  default_repo_by_system: Partial<Record<BugReportSystem, string>>
}

export type GitHubIssueType = 'bug' | 'feature' | 'chore' | 'intent'

/** The issue-writer draft from cdb_tqs, as the BFF returns it. */
export interface BugReportGitHubDraft {
  type: GitHubIssueType
  title: string
  body: string
  /** Every suggested label, known ones in the repo's spelling (#1135). */
  labels: string[]
  repo: string
  /** The repo's labels; `null`/missing when GitHub could not be asked. */
  available_labels?: string[] | null
  /** Suggested labels the repo does not have (#1135). */
  unknown_labels?: string[]
}

export interface CreateGitHubIssueData {
  repo: string
  title: string
  body: string
  labels: string[]
  includeInternalNotes: boolean
}

export interface CreateGitHubIssueResult {
  repo: string
  number: number
  url: string
  created_at: string
  report: BugReport
  event: BugReportMessage
}

/** Error with the BFF's stable `code` where it sends one (`detail: {code,
 *  message}`), e.g. `report_closed`. `message` is the server text or a
 *  `BUG_REPORT_*` fallback, as before. */
export class BugReportApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** `unknown_labels` (400, #1135): the labels the target repo lacks. */
    readonly labels?: string[],
  ) {
    super(message)
    this.name = 'BugReportApiError'
  }
}

/** `reply` goes between team and reporter, `internal_note` stays in the team
 *  (the API never returns one to the reporter), `event` records a status
 *  change. */
export type BugReportMessageKind = 'reply' | 'internal_note' | 'event'

export interface BugReportMessage {
  id: string
  report_id: string
  kind: BugReportMessageKind
  body: string
  attachments: BugReportAttachment[]
  /** `null` for notes migrated from `admin_notes`, after the author's deletion
   *  and once a thread was anonymised (#1107). */
  author_user_id: string | null
  author_name: string | null
  /** The author is the report's reporter (otherwise: the team). */
  from_reporter: boolean
  /** Events only. */
  from_status: BugReportStatus | null
  to_status: BugReportStatus | null
  /** Type-change events only (#1168): visible to the reporter too. */
  from_type: TicketType | null
  to_type: TicketType | null
  /** On the event that closed the ticket (#1168). */
  close_reason: TicketCloseReason | null
  created_at: string
}

export interface PostBugReportMessageData {
  /** Default `reply`. `internal_note` is admin-only (403 otherwise). */
  kind?: 'reply' | 'internal_note'
  body?: string
  /** Uploaded first through the bug-report upload, same limits as a report. */
  files?: File[]
  /** Admin-only: change the status in the same request. */
  status?: BugReportStatus
  /** Required with `status: 'closed'` (#1168), ignored otherwise. */
  closeReason?: TicketCloseReason
}

export interface PostBugReportMessageResult {
  message: BugReportMessage
  /** The status event the post caused, if any — also the automatic
   *  `waiting_for_reporter` → `in_progress` on a reporter reply. */
  event: BugReportMessage | null
  report: BugReport
}

/** `PUT /bug-reports/{id}/type` (#1168). */
export interface ChangeTicketTypeResult {
  report: BugReport
  /** The type-change event; `null` when the ticket already had that type. */
  event: BugReportMessage | null
  /** A `planned` feature that becomes a bug moves to `open`: its status event. */
  status_event: BugReportMessage | null
}

export interface BugReportReadResult {
  report_id: string
  last_read_at: string
  unread_count: number
}

/** `GET /bug-reports/unread-count` (#1135): admins count every report, everyone else their own. */
export interface BugReportUnreadCount {
  reports_with_unread: number
}

export interface CreateBugReportData {
  title: string
  description: string
  /** #1168: default `bug`. */
  type?: TicketType
  /** Must belong to `type` (`categoriesForType`). */
  category: TicketCategory
  system: BugReportSystem
  /** #1168: optional, see `TicketContext`. */
  context?: TicketContext
  files?: File[]
}

/** `GET /bug-reports/similar` (#1172): a ticket whose title resembles the one
 *  being typed. Non-admins only ever get their own tickets. */
export interface SimilarTicket {
  id: string
  type: TicketType
  title: string
  category: TicketCategory
  status: BugReportStatus
  close_reason: TicketCloseReason | null
  created_at: string
  updated_at: string
  /** The caller filed it ("von Ihnen gemeldet"). */
  own: boolean
  /** Admins only. */
  client_name: string | null
  /** Own and not closed: "Ergänzen" posts a reply to it. */
  can_append: boolean
  score: number
}

/** `POST /bug-reports/classify` (#1172): a hint, never applied on its own. */
export interface TicketSuggestion {
  type: TicketType
  category: TicketCategory
  /** 0..1 */
  confidence: number
}

/** List filters (#1168): both types when `type` is omitted. */
export interface BugReportListFilters {
  status?: BugReportStatus
  type?: TicketType
}

function listQuery(filters: Record<string, string | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value)
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

/** `close_reason` for a status change: required when closing (#1168), never
 *  sent otherwise (the BFF answers 422 to a reason without a close). Every
 *  close path of the UI names one (#1171), so a close without a reason is a
 *  programming error, refused before anything is sent. */
function closeReasonFor(status: BugReportStatus | undefined, reason?: TicketCloseReason): { close_reason?: TicketCloseReason } {
  if (status !== 'closed') return {}
  if (!reason) throw new Error('BUG_REPORT_CLOSE_REASON_REQUIRED')
  return { close_reason: reason }
}

function validateFiles(files: File[]): void {
  if (files.length > MAX_ATTACHMENTS) {
    throw new Error('BUG_REPORT_MAX_FILES_EXCEEDED')
  }

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error('BUG_REPORT_FILE_TOO_LARGE')
    }
    if (!isAllowedAttachment(file)) {
      throw new Error('BUG_REPORT_INVALID_FILE_TYPE')
    }
  }
}

async function uploadAttachments(files: File[]): Promise<BugReportAttachment[]> {
  if (files.length === 0) return []

  const token = getAccessToken()
  if (!token) throw new Error('BUG_REPORT_NOT_AUTHENTICATED')

  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))

  const response = await fetch(UPLOAD_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Upload failed' }))
    throw new Error(err.detail || 'BUG_REPORT_UPLOAD_FAILED')
  }

  return response.json() as Promise<BugReportAttachment[]>
}

async function apiError(response: Response, fallbackCode: string): Promise<BugReportApiError> {
  const err = await response.json().catch(() => ({ detail: undefined }))
  const detail = err?.detail
  if (detail && typeof detail === 'object') {
    const labels = Array.isArray(detail.labels) ? detail.labels.filter((l: unknown) => typeof l === 'string') : undefined
    return new BugReportApiError(detail.message || fallbackCode, response.status, detail.code, labels)
  }
  return new BugReportApiError(typeof detail === 'string' && detail ? detail : fallbackCode, response.status)
}

async function readJson<T>(response: Response, fallbackCode: string): Promise<T> {
  if (!response.ok) throw await apiError(response, fallbackCode)
  return (await response.json()) as T
}

async function deleteAttachments(attachments: BugReportAttachment[]): Promise<void> {
  if (attachments.length === 0) return

  const token = getAccessToken()
  if (!token) return

  await fetch(UPLOAD_ENDPOINT, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ paths: attachments.map((a) => a.path) }),
  }).catch(() => {
    // Best-effort cleanup – do not throw
  })
}

export const bugReportService = {
  maxAttachments: MAX_ATTACHMENTS,
  maxFileSizeBytes: MAX_FILE_SIZE_BYTES,

  async createReport(data: CreateBugReportData): Promise<BugReport> {
    const token = getAccessToken()
    if (!token) throw new Error('BUG_REPORT_NOT_AUTHENTICATED')

    const title = data.title.trim()
    const description = data.description.trim()

    if (!title) throw new Error('BUG_REPORT_TITLE_REQUIRED')
    if (!description) throw new Error('BUG_REPORT_DESCRIPTION_REQUIRED')
    if (!data.system) throw new Error('BUG_REPORT_SYSTEM_REQUIRED')

    const files = data.files ?? []
    validateFiles(files)

    let attachments: BugReportAttachment[] = []
    try {
      // 1. Upload attachments to Azure Blob Storage
      attachments = await uploadAttachments(files)

      // 2. Create report via BFF
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title,
          description,
          type: data.type ?? 'bug',
          category: data.category,
          system: data.system,
          ...(data.context ? { context: data.context } : {}),
        }),
      })

      // `ticket_type_disabled` (403, #1544) arrives as a code.
      if (!response.ok) throw await apiError(response, 'BUG_REPORT_CREATE_FAILED')

      const report = (await response.json()) as BugReport

      // 3. If there are attachments, update the report with attachment metadata
      if (attachments.length > 0) {
        const patchResponse = await fetch(`${API_BASE}/${report.id}/attachments`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(attachments),
        })

        if (patchResponse.ok) {
          return (await patchResponse.json()) as BugReport
        }
      }

      return report
    } catch (error) {
      await deleteAttachments(attachments)
      throw error
    }
  },

  /** Every ticket (admin only); `filters.type` narrows to bugs or features. */
  async getAllReports(filters: BugReportListFilters = {}): Promise<BugReport[]> {
    const params = listQuery({ status_filter: filters.status, type: filters.type })
    const response = await fetch(`${API_BASE}${params}`, {
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Load failed' }))
      throw new Error(err.detail || 'BUG_REPORT_LOAD_FAILED')
    }

    const data = (await response.json()) as { reports: BugReport[]; total: number }
    return data.reports
  },

  /** The caller's own tickets, newest first (#1092); both types unless `type` is given. */
  async getMyReports(type?: TicketType): Promise<BugReport[]> {
    const response = await fetch(`${API_BASE}/mine${listQuery({ type })}`, {
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Load failed' }))
      throw new Error(err.detail || 'BUG_REPORT_LOAD_FAILED')
    }

    const data = (await response.json()) as { reports: BugReport[]; total: number }
    return data.reports
  },

  /** Status change (writes an event, notifies the reporter). `adminNotes` is
   *  the legacy notes field: a changed value is stored as a new internal note,
   *  an empty one deletes nothing (#1094). A changed note on a report that is
   *  and stays closed fails with code `report_closed`. Closing requires
   *  `closeReason` (#1168, thrown as `BUG_REPORT_CLOSE_REASON_REQUIRED`
   *  without one); `planned` on a bug fails with code `planned_not_allowed`. */
  async updateStatus(
    reportId: string,
    status: BugReportStatus,
    adminNotes?: string,
    closeReason?: TicketCloseReason,
  ): Promise<void> {
    const reason = closeReasonFor(status, closeReason)
    const response = await fetch(`${API_BASE}/${reportId}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        status,
        admin_notes: adminNotes !== undefined ? adminNotes : null,
        ...reason,
      }),
    })

    if (!response.ok) throw await apiError(response, 'BUG_REPORT_UPDATE_FAILED')
  },

  /** Reclassify a ticket (#1168, admin): writes an event the reporter sees and
   *  notifies them (`bug_report_type_changed`). `category` must belong to the
   *  new type; without it the BFF maps the current one. Same type: no-op. */
  async changeType(reportId: string, type: TicketType, category?: TicketCategory): Promise<ChangeTicketTypeResult> {
    const response = await fetch(`${API_BASE}/${reportId}/type`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ type, ...(category ? { category } : {}) }),
    })
    return readJson<ChangeTicketTypeResult>(response, 'BUG_REPORT_TYPE_CHANGE_FAILED')
  },

  /** One report with the caller's unread count (#1094). Reporter or admin;
   *  someone else's report answers 404 like a missing one. */
  async getReport(reportId: string): Promise<BugReport> {
    const response = await fetch(`${API_BASE}/${reportId}`, { headers: getAuthHeaders() })
    return readJson<BugReport>(response, 'BUG_REPORT_LOAD_FAILED')
  },

  /** The thread, oldest first. Internal notes only for admins (#1094). */
  async getMessages(reportId: string): Promise<BugReportMessage[]> {
    const response = await fetch(`${API_BASE}/${reportId}/messages`, { headers: getAuthHeaders() })
    const data = await readJson<{ messages: BugReportMessage[]; total: number }>(
      response,
      'BUG_REPORT_MESSAGES_LOAD_FAILED',
    )
    return data.messages
  },

  /**
   * Reply or internal note (#1094), optionally with files and — admins only —
   * a status change. Files are uploaded first; if the post fails they are
   * deleted again, like on report creation. A closed report is read-only:
   * `BugReportApiError` with code `report_closed` (409), unless an admin
   * reopens it with `status` in the same call. A reporter reply on a
   * `waiting_for_reporter` or `resolved` report moves it to `in_progress`.
   * Closing requires `closeReason` (#1168).
   */
  async postMessage(reportId: string, data: PostBugReportMessageData): Promise<PostBugReportMessageResult> {
    const body = data.body?.trim() ?? ''
    const files = data.files ?? []
    if (!body && files.length === 0) throw new Error('BUG_REPORT_MESSAGE_EMPTY')
    validateFiles(files)
    const statusChange = data.status ? { status: data.status, ...closeReasonFor(data.status, data.closeReason) } : {}

    let attachments: BugReportAttachment[] = []
    try {
      attachments = await uploadAttachments(files)
      const response = await fetch(`${API_BASE}/${reportId}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          kind: data.kind ?? 'reply',
          body,
          attachments,
          ...statusChange,
        }),
      })
      const result = await readJson<PostBugReportMessageResult>(response, 'BUG_REPORT_MESSAGE_FAILED')
      announceRead(reportId)
      return result
    } catch (error) {
      await deleteAttachments(attachments)
      throw error
    }
  },

  /** Tickets similar to a title being typed (#1172), best first. Admins
   *  search every ticket, everyone else only their own. */
  async findSimilar(q: string, type?: TicketType, signal?: AbortSignal): Promise<SimilarTicket[]> {
    const response = await fetch(`${API_BASE}/similar${listQuery({ q, type })}`, {
      headers: getAuthHeaders(),
      signal,
    })
    const data = await readJson<{ tickets: SimilarTicket[] }>(response, 'BUG_REPORT_SIMILAR_FAILED')
    return data.tickets
  },

  /** Type/category hint for a ticket being written (#1172). Only title and
   *  description are sent; cdb_tqs strips e-mail addresses and ids. */
  async classifyTicket(
    data: { title: string; description: string },
    signal?: AbortSignal,
  ): Promise<TicketSuggestion> {
    const response = await fetch(`${API_BASE}/classify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title: data.title.trim().slice(0, 200), description: data.description.trim().slice(0, 5000) }),
      signal,
    })
    return readJson<TicketSuggestion>(response, 'BUG_REPORT_CLASSIFY_FAILED')
  },

  /** GitHub issue creation (#1136): available here? which repos? Admin only. */
  async getGitHubConfig(): Promise<BugReportGitHubConfig> {
    const response = await fetch(`${API_BASE}/github/config`, { headers: getAuthHeaders() })
    return readJson<BugReportGitHubConfig>(response, 'BUG_REPORT_GITHUB_CONFIG_FAILED')
  },

  /**
   * Draft an issue from the report and its thread (#1136). The BFF sends the
   * report without reporter identity to cdb_tqs; internal notes only when
   * `includeInternalNotes`. Takes a few seconds. Codes: `github_not_configured`
   * (503), `github_issue_exists` (409), `repo_not_allowed` (400),
   * `draft_unavailable` (502).
   */
  async draftGitHubIssue(
    reportId: string,
    options: { repo?: string; includeInternalNotes: boolean },
  ): Promise<BugReportGitHubDraft> {
    const response = await fetch(`${API_BASE}/${reportId}/github/draft`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        include_internal_notes: options.includeInternalNotes,
        ...(options.repo ? { repo: options.repo } : {}),
        ticket_base_url: window.location.origin,
      }),
    })
    return readJson<BugReportGitHubDraft>(response, 'BUG_REPORT_GITHUB_DRAFT_FAILED')
  },

  /** Create the reviewed issue (#1136). 409 `github_issue_exists` when the
   *  report already has one, 400 `unknown_labels` (with `labels`) when the
   *  repo lacks a label (#1135), 502 `github_failed` when GitHub refuses. */
  async createGitHubIssue(reportId: string, data: CreateGitHubIssueData): Promise<CreateGitHubIssueResult> {
    const response = await fetch(`${API_BASE}/${reportId}/github/issue`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        repo: data.repo,
        title: data.title,
        body: data.body,
        labels: data.labels,
        include_internal_notes: data.includeInternalNotes,
      }),
    })
    return readJson<CreateGitHubIssueResult>(response, 'BUG_REPORT_GITHUB_CREATE_FAILED')
  },

  /** Mark the thread as read for the caller (#1094). */
  async markRead(reportId: string): Promise<BugReportReadResult> {
    const response = await fetch(`${API_BASE}/${reportId}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    const result = await readJson<BugReportReadResult>(response, 'BUG_REPORT_MARK_READ_FAILED')
    announceRead(reportId)
    return result
  },

  /** How many reports have replies the caller has not read — the Cortex badge
   *  (#1135). `scope: 'mine'` counts only the caller's own tickets, admins
   *  included (the reporter view, #1176); default: everything an admin sees. */
  async getUnreadCount(scope: 'all' | 'mine' = 'all'): Promise<number> {
    const query = scope === 'mine' ? '?scope=mine' : ''
    const response = await fetch(`${API_BASE}/unread-count${query}`, { headers: getAuthHeaders() })
    const result = await readJson<BugReportUnreadCount>(response, 'BUG_REPORT_UNREAD_COUNT_FAILED')
    return result.reports_with_unread
  },

  /**
   * An attachment's bytes as a blob URL, fetched with the admin's token.
   *
   * Attachments are admin-only since #1066; a plain `<img src>` or
   * `<video src>` on the asset URL carries no Authorization header, so every
   * surface loads them through this. The caller owns the URL and must revoke it.
   */
  async fetchAttachmentObjectUrl(path: string): Promise<string> {
    const response = await fetch(`${ASSETS_BASE}/${path}`, { headers: getAuthHeaders() })
    if (!response.ok) throw new Error('BUG_REPORT_ATTACHMENT_FAILED')
    return URL.createObjectURL(await response.blob())
  },

  /**
   * The attachment converted to PDF server-side, as a blob URL.
   *
   * The fallback for Word that mammoth cannot read — legacy `.doc`, or a
   * `.docx` whose conversion throws. Runs through the BFF to TQS/Collabora.
   * Fetched with auth headers because that endpoint is admin-only, which a
   * bare react-pdf fetch could not satisfy.
   *
   * The caller owns the returned URL and must revoke it.
   */
  async fetchConvertedPdf(attachment: BugReportAttachment): Promise<string> {
    const response = await fetch(`${API_BASE}/attachments/${attachment.path}/preview`, {
      headers: getAuthHeaders(),
    })
    if (!response.ok) throw new Error('BUG_REPORT_PREVIEW_FAILED')
    return URL.createObjectURL(await response.blob())
  },

  /**
   * Download an attachment under its original filename.
   *
   * Goes through a blob rather than a plain `<a download>`: the BFF sits on a
   * different origin than the frontend, and browsers ignore the `download`
   * attribute for cross-origin URLs — the file would open in a tab instead of
   * saving, and under the UUID blob name at that.
   */
  async downloadAttachment(attachment: BugReportAttachment): Promise<void> {
    await saveAs(`${ASSETS_BASE}/${attachment.path}`, attachment.name)
  },

  /**
   * Download an attachment of one report — the reporter's path (#1098). The
   * asset route above is admin-only; this one lets the reporter fetch the
   * files of their own report and of its visible thread messages. Admins may
   * use it too.
   */
  async downloadReportAttachment(reportId: string, attachment: BugReportAttachment): Promise<void> {
    await saveAs(reportAttachmentUrl(reportId, attachment.path), attachment.name)
  },

  /** An attachment of one report as a blob URL, through the reporter route
   *  (#1135) — what the reporter's lightbox and preview render. The caller owns
   *  the URL and must revoke it. */
  async fetchReportAttachmentObjectUrl(reportId: string, path: string): Promise<string> {
    return fetchObjectUrl(reportAttachmentUrl(reportId, path), 'BUG_REPORT_ATTACHMENT_FAILED')
  },

  /** A Word attachment of one report converted to PDF, through the reporter
   *  route with the same visibility check as the download (#1135). The caller
   *  owns the URL and must revoke it. */
  async fetchReportConvertedPdf(reportId: string, attachment: BugReportAttachment): Promise<string> {
    return fetchObjectUrl(`${reportAttachmentUrl(reportId, attachment.path)}/preview`, 'BUG_REPORT_PREVIEW_FAILED')
  },
}

function reportAttachmentUrl(reportId: string, path: string): string {
  return `${API_BASE}/${reportId}/attachments/${encodeURIComponent(path)}`
}

async function fetchObjectUrl(url: string, errorCode: string): Promise<string> {
  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) throw new Error(errorCode)
  return URL.createObjectURL(await response.blob())
}

/** Fetch with the caller's token and save under `fileName` (why a blob: see
 *  `downloadAttachment`). */
async function saveAs(url: string, fileName: string): Promise<void> {
  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) throw new Error('BUG_REPORT_DOWNLOAD_FAILED')

  const objectUrl = URL.createObjectURL(await response.blob())
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // Revoking synchronously can race the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  }
}
